import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, MenuItem, TextField, Switch, Alert,
  CircularProgress, Divider, InputAdornment, Chip, FormControlLabel,
} from '@mui/material';
import { SaveOutlined, AccountTreeOutlined, AutoAwesome } from '@mui/icons-material';
import {
  P, cardSx, fieldSx, priBtn, outBtn, PX, FieldLabel, StatusPill, WorkspaceHeader, ValidationStrip,
} from '../../components/common/workspaceKit';
import { financeService } from '../../services/financeService';
import { coaService, CoaAccount } from '../../services/coaService';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { projectService } from '../../services/projectService';

const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
const TYPE_BASE: Record<string, number> = { ASSET: 1000, LIABILITY: 2000, EQUITY: 3000, INCOME: 4000, EXPENSE: 5000 };
const DEBIT_TYPES = ['ASSET', 'EXPENSE'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'JPY', 'AED', 'SGD', 'AUD'];
const RECON_TYPES = ['BANK', 'CUSTOMER', 'VENDOR', 'GENERAL'];
const STATEMENT_OF: Record<string, string> = { ASSET: 'Balance Sheet', LIABILITY: 'Balance Sheet', EQUITY: 'Balance Sheet', INCOME: 'Profit & Loss', EXPENSE: 'Profit & Loss' };

interface Cfg {
  accountCode: string; accountName: string; accountType: string; parentId: string; description: string;
  normalBalance: 'DR' | 'CR'; isGroup: boolean; currencyPolicy: 'ALL' | 'BASE' | 'SPECIFIC'; currencyCode: string;
  allowManualPosting: boolean; reconciliationRequired: boolean; reconciliationType: string;
  costCenterRequired: boolean; defaultCostCenterId: string;
  projectRequired: boolean; defaultProjectId: string;
  profitCenterRequired: boolean; defaultProfitCenterId: string;
  departmentRequired: boolean;
  gstApplicable: boolean; tdsApplicable: boolean;
  accountStatus: 'ACTIVE' | 'INACTIVE' | 'BLOCKED'; isControlAccount: boolean; requireApproval: boolean; fxRevaluation: boolean;
  notes: string;
}
const blank: Cfg = {
  accountCode: '', accountName: '', accountType: 'ASSET', parentId: '', description: '',
  normalBalance: 'DR', isGroup: false, currencyPolicy: 'ALL', currencyCode: 'INR',
  allowManualPosting: true, reconciliationRequired: false, reconciliationType: 'GENERAL',
  costCenterRequired: false, defaultCostCenterId: '', projectRequired: false, defaultProjectId: '',
  profitCenterRequired: false, defaultProfitCenterId: '', departmentRequired: false,
  gstApplicable: false, tdsApplicable: false,
  accountStatus: 'ACTIVE', isControlAccount: false, requireApproval: false, fxRevaluation: false, notes: '',
};

const SectionCard: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${P.line}` }}>
      <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: P.strong }}>{title}</Typography>
      {sub && <Typography sx={{ fontSize: '12px', color: P.muted, mt: '2px' }}>{sub}</Typography>}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);
const Toggle: React.FC<{ label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ label, hint, checked, onChange, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: P.strong }}>{label}</Typography>
      {hint && <Typography sx={{ fontSize: '11.5px', color: P.muted }}>{hint}</Typography>}
    </Box>
    <Switch size="small" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
  </Box>
);
const grid3 = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: '16px' } as const;
const grid2 = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' }, gap: '16px' } as const;

const AccountWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const parentParam = searchParams.get('parent');
  const editId = id ? Number(id) : null;
  const isEdit = !!editId;
  const [parentApplied, setParentApplied] = useState(false);

  const [f, setF] = useState<Cfg>(blank);
  const [accounts, setAccounts] = useState<CoaAccount[]>([]);
  const [costCenters, setCostCenters] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [profitCenters, setProfitCenters] = useState<any[]>([]);
  const [codeTouched, setCodeTouched] = useState(false);
  const [normalTouched, setNormalTouched] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<Cfg>) => setF((s) => ({ ...s, ...patch }));

  useEffect(() => {
    coaService.accounts().then((r) => setAccounts(r.accounts || [])).catch(() => {});
    financeWorkspaceService.listCostCenters().then((r) => setCostCenters(r.costCenters || [])).catch(() => {});
    financeWorkspaceService.listProfitCenters().then((r) => setProfitCenters(r.profitCenters || [])).catch(() => {});
    projectService.getProjects().then((r: any) => setProjects(Array.isArray(r) ? r : (r?.projects || []))).catch(() => {});
  }, []);

  // Preload for edit.
  useEffect(() => {
    if (!editId) return;
    setLoading(true);
    financeService.getAccountById(editId).then((a: any) => {
      setCodeTouched(true); setNormalTouched(true);
      set({
        accountCode: a.account_code || '', accountName: a.account_name || '', accountType: a.account_type || 'ASSET',
        parentId: a.parent_id ? String(a.parent_id) : '', description: a.description || '',
        normalBalance: a.normal_balance === 'CR' ? 'CR' : 'DR', isGroup: !!a.is_group,
        currencyPolicy: (a.currency_policy as any) || 'ALL', currencyCode: a.currency_code || 'INR',
        allowManualPosting: a.allow_manual_posting !== 0, reconciliationRequired: !!a.reconciliation_required,
        reconciliationType: a.reconciliation_type || 'GENERAL',
        costCenterRequired: !!a.cost_center_required, defaultCostCenterId: a.default_cost_center_id ? String(a.default_cost_center_id) : '',
        projectRequired: !!a.project_required, defaultProjectId: a.default_project_id ? String(a.default_project_id) : '',
        profitCenterRequired: !!a.profit_center_required, defaultProfitCenterId: a.default_profit_center_id ? String(a.default_profit_center_id) : '',
        departmentRequired: !!a.department_required,
        gstApplicable: !!a.gst_applicable, tdsApplicable: !!a.tds_applicable,
        accountStatus: (a.account_status as any) || (a.is_active ? 'ACTIVE' : 'INACTIVE'),
        isControlAccount: !!a.is_control_account, requireApproval: !!a.require_approval, fxRevaluation: !!a.fx_revaluation,
        notes: a.notes || '',
      });
      setError(null);
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [editId]);

  // Preselect the parent (and inherit its type) when opened as "New Sub-Account".
  useEffect(() => {
    if (isEdit || parentApplied || !parentParam || !accounts.length) return;
    const p = accounts.find((a) => a.id === Number(parentParam));
    if (p) { set({ parentId: String(p.id), accountType: p.account_type }); setParentApplied(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, parentParam, isEdit, parentApplied]);

  // Derive normal balance from type unless the user overrode it.
  useEffect(() => {
    if (!normalTouched) set({ normalBalance: DEBIT_TYPES.includes(f.accountType) ? 'DR' : 'CR' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.accountType]);

  const suggestCode = useMemo(() => {
    const base = TYPE_BASE[f.accountType] || 1000;
    const nums = accounts.filter((a) => a.account_type === f.accountType)
      .map((a) => parseInt(String(a.account_code).replace(/\D/g, ''), 10))
      .filter((n) => !Number.isNaN(n) && n >= base && n < base + 1000);
    return String(nums.length ? Math.max(...nums) + 10 : base + 10);
  }, [accounts, f.accountType]);

  // Auto-suggest a code in create mode until the user edits it.
  useEffect(() => {
    if (!isEdit && !codeTouched) set({ accountCode: suggestCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestCode, isEdit, codeTouched]);

  const codeUpper = f.accountCode.trim().toUpperCase();
  const dupCode = !!codeUpper && accounts.some((a) => a.account_code.toUpperCase() === codeUpper && a.id !== editId);
  const dupName = !!f.accountName.trim() && accounts.some((a) => a.account_name.trim().toLowerCase() === f.accountName.trim().toLowerCase() && a.id !== editId);
  const parentOptions = accounts.filter((a) => a.account_type === f.accountType && a.id !== editId);
  const currencyBad = f.currencyPolicy === 'SPECIFIC' && !f.currencyCode;

  const checks = [
    { ok: !!codeUpper, label: codeUpper ? `Code ${codeUpper}` : 'Account code required' },
    { ok: !dupCode, label: dupCode ? 'Code already exists' : 'Code is unique' },
    { ok: !!f.accountName.trim(), label: f.accountName.trim() ? 'Name set' : 'Name required' },
    { ok: !currencyBad, label: currencyBad ? 'Pick a specific currency' : `Currency: ${f.currencyPolicy === 'SPECIFIC' ? f.currencyCode : f.currencyPolicy}` },
  ];
  const canSave = !!codeUpper && !dupCode && !!f.accountName.trim() && !currencyBad && !saving;

  const save = async () => {
    setSaving(true); setError(null);
    const payload: Record<string, any> = {
      accountCode: codeUpper, accountName: f.accountName.trim(), accountType: f.accountType,
      parentId: f.parentId ? Number(f.parentId) : null, description: f.description || null,
      normalBalance: f.normalBalance, isGroup: f.isGroup, accountStatus: f.accountStatus,
      currencyPolicy: f.currencyPolicy, currencyCode: f.currencyPolicy === 'SPECIFIC' ? f.currencyCode : null, fxRevaluation: f.fxRevaluation,
      allowManualPosting: f.allowManualPosting, isControlAccount: f.isControlAccount, requireApproval: f.requireApproval,
      reconciliationRequired: f.reconciliationRequired, reconciliationType: f.reconciliationRequired ? f.reconciliationType : null,
      costCenterRequired: f.costCenterRequired, defaultCostCenterId: f.defaultCostCenterId || null,
      projectRequired: f.projectRequired, defaultProjectId: f.defaultProjectId || null,
      profitCenterRequired: f.profitCenterRequired, defaultProfitCenterId: f.defaultProfitCenterId || null,
      departmentRequired: f.departmentRequired,
      gstApplicable: f.gstApplicable, tdsApplicable: f.tdsApplicable, notes: f.notes || null,
    };
    try {
      if (isEdit) await financeService.updateAccountConfigured(editId!, payload);
      else await financeService.createAccountConfigured(payload);
      navigate('/finance/accounts');
    } catch (e: any) { setError(e?.message || 'Failed to save account'); setSaving(false); }
  };

  const parentName = f.parentId ? (accounts.find((a) => a.id === Number(f.parentId))?.account_name || '') : '';

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100%', pb: 6, color: P.text }}>
      <WorkspaceHeader
        onBack={() => navigate('/finance/accounts')}
        title={isEdit ? 'Edit Account' : 'New Account'}
        subtitle="Create and configure a ledger account — posting rules, currency, dimensions and tax."
        chip={<StatusPill label={f.accountStatus} tone={f.accountStatus === 'ACTIVE' ? 'green' : f.accountStatus === 'BLOCKED' ? 'red' : 'muted'} />}
        actions={(
          <>
            <Button variant="outlined" sx={outBtn} onClick={() => navigate('/finance/accounts')} disabled={saving}>Cancel</Button>
            <Button variant="contained" sx={priBtn} onClick={save} disabled={!canSave}
              startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveOutlined sx={{ fontSize: 18 }} />}>
              {isEdit ? 'Save changes' : 'Create account'}
            </Button>
          </>
        )}
      />
      <ValidationStrip items={checks} />

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ px: PX, pt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 320px' }, gap: 2.5, alignItems: 'start' }}>
          <Box sx={{ minWidth: 0 }}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <SectionCard title="Basic Information">
              <Box sx={grid3}>
                <Box>
                  <FieldLabel text="Account Code" required />
                  <TextField fullWidth size="small" value={f.accountCode} onChange={(e) => { setCodeTouched(true); set({ accountCode: e.target.value }); }}
                    error={dupCode} helperText={dupCode ? 'Code already exists' : (!isEdit ? `Suggested: ${suggestCode}` : ' ')} disabled={isEdit}
                    InputProps={{ endAdornment: (!isEdit && f.accountCode !== suggestCode) ? <InputAdornment position="end"><Button size="small" onClick={() => { setCodeTouched(true); set({ accountCode: suggestCode }); }} sx={{ minWidth: 0, textTransform: 'none', fontSize: 11 }} startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}>Auto</Button></InputAdornment> : undefined }}
                    sx={fieldSx} />
                </Box>
                <Box>
                  <FieldLabel text="Account Type" required />
                  <TextField select fullWidth size="small" value={f.accountType} onChange={(e) => set({ accountType: e.target.value, parentId: '' })} disabled={isEdit} sx={fieldSx}>
                    {ACCOUNT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Box>
                <Box>
                  <FieldLabel text="Status" />
                  <TextField select fullWidth size="small" value={f.accountStatus} onChange={(e) => set({ accountStatus: e.target.value as any })} sx={fieldSx}>
                    <MenuItem value="ACTIVE">Active</MenuItem><MenuItem value="INACTIVE">Inactive</MenuItem><MenuItem value="BLOCKED">Blocked</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ gridColumn: { xs: '1', md: 'span 2' } }}>
                  <FieldLabel text="Account Name" required />
                  <TextField fullWidth size="small" value={f.accountName} onChange={(e) => set({ accountName: e.target.value })}
                    error={false} helperText={dupName ? 'A similar account name already exists' : ' '} sx={fieldSx} />
                </Box>
                <Box>
                  <FieldLabel text="Parent Account" />
                  <TextField select fullWidth size="small" value={f.parentId} onChange={(e) => set({ parentId: e.target.value })} sx={fieldSx}
                    helperText={parentOptions.length ? ' ' : 'No same-type parents yet'}>
                    <MenuItem value="">— None (top level) —</MenuItem>
                    {parentOptions.map((a) => <MenuItem key={a.id} value={a.id}>{a.account_code} · {a.account_name}</MenuItem>)}
                  </TextField>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <FieldLabel text="Description" />
                  <TextField fullWidth size="small" value={f.description} onChange={(e) => set({ description: e.target.value })} placeholder="What is this account used for?" sx={fieldSx} />
                </Box>
              </Box>
            </SectionCard>

            <SectionCard title="Accounting Configuration" sub="Posting rules honoured by the Journal Entry engine.">
              <Box sx={grid3}>
                <Box>
                  <FieldLabel text="Normal Balance" />
                  <TextField select fullWidth size="small" value={f.normalBalance} onChange={(e) => { setNormalTouched(true); set({ normalBalance: e.target.value as any }); }} sx={fieldSx}
                    helperText={`Suggested ${DEBIT_TYPES.includes(f.accountType) ? 'Debit' : 'Credit'} for ${f.accountType}`}>
                    <MenuItem value="DR">Debit</MenuItem><MenuItem value="CR">Credit</MenuItem>
                  </TextField>
                </Box>
                <Box>
                  <FieldLabel text="Account Level" />
                  <TextField select fullWidth size="small" value={f.isGroup ? 'GROUP' : 'POSTING'} onChange={(e) => set({ isGroup: e.target.value === 'GROUP' })} sx={fieldSx}
                    helperText={f.isGroup ? 'Group — no direct postings' : 'Posting — accepts entries'}>
                    <MenuItem value="POSTING">Posting Account</MenuItem><MenuItem value="GROUP">Group / Header</MenuItem>
                  </TextField>
                </Box>
                <Box>
                  <FieldLabel text="Currency Policy" />
                  <TextField select fullWidth size="small" value={f.currencyPolicy} onChange={(e) => set({ currencyPolicy: e.target.value as any })} sx={fieldSx}>
                    <MenuItem value="ALL">All Currencies</MenuItem><MenuItem value="BASE">Base Currency Only</MenuItem><MenuItem value="SPECIFIC">Specific Currency</MenuItem>
                  </TextField>
                </Box>
                {f.currencyPolicy === 'SPECIFIC' && (
                  <Box>
                    <FieldLabel text="Currency" required />
                    <TextField select fullWidth size="small" value={f.currencyCode} onChange={(e) => set({ currencyCode: e.target.value })} sx={fieldSx}>
                      {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                  </Box>
                )}
              </Box>
              <Divider sx={{ my: 2, borderColor: P.line }} />
              <Box sx={grid2}>
                <Toggle label="Allow manual journal posting" hint="Uncheck for system-only control accounts" checked={f.allowManualPosting} onChange={(v) => set({ allowManualPosting: v })} />
                <Toggle label="Foreign-currency revaluation" hint="Include in period-end FX revaluation" checked={f.fxRevaluation} onChange={(v) => set({ fxRevaluation: v })} disabled={f.currencyPolicy === 'BASE'} />
                <Toggle label="Reconciliation required" hint="Bank / customer / vendor / control accounts" checked={f.reconciliationRequired} onChange={(v) => set({ reconciliationRequired: v })} />
                {f.reconciliationRequired && (
                  <Box>
                    <FieldLabel text="Reconciliation Type" />
                    <TextField select fullWidth size="small" value={f.reconciliationType} onChange={(e) => set({ reconciliationType: e.target.value })} sx={fieldSx}>
                      {RECON_TYPES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </TextField>
                  </Box>
                )}
              </Box>
            </SectionCard>

            <SectionCard title="Dimensions & Allocation" sub="Required = the Journal Entry engine blocks posting when missing. Default = pre-fills the line.">
              <Stack spacing={1.5}>
                {([
                  ['Cost Center', 'costCenterRequired', 'defaultCostCenterId', costCenters, (c: any) => `${c.code} · ${c.name}`],
                  ['Profit Center', 'profitCenterRequired', 'defaultProfitCenterId', profitCenters, (c: any) => `${c.code} · ${c.name}`],
                  ['Project', 'projectRequired', 'defaultProjectId', projects, (c: any) => `${c.project_code || c.projectCode || ''} · ${c.project_name || c.projectName}`],
                ] as const).map(([label, reqKey, defKey, opts, fmt]) => (
                  <Box key={label} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px 160px 1fr' }, gap: 1.5, alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>{label}</Typography>
                    <TextField select size="small" value={(f as any)[reqKey] ? 'REQUIRED' : 'OPTIONAL'} onChange={(e) => set({ [reqKey]: e.target.value === 'REQUIRED' } as any)} sx={fieldSx}>
                      <MenuItem value="OPTIONAL">Optional</MenuItem><MenuItem value="REQUIRED">Required</MenuItem>
                    </TextField>
                    <TextField select size="small" value={(f as any)[defKey]} onChange={(e) => set({ [defKey]: e.target.value } as any)} sx={fieldSx} label="Default (optional)">
                      <MenuItem value="">— None —</MenuItem>
                      {opts.map((o: any) => <MenuItem key={o.id} value={o.id}>{fmt(o)}</MenuItem>)}
                    </TextField>
                  </Box>
                ))}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px 160px 1fr' }, gap: 1.5, alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Department</Typography>
                  <TextField select size="small" value={f.departmentRequired ? 'REQUIRED' : 'OPTIONAL'} onChange={(e) => set({ departmentRequired: e.target.value === 'REQUIRED' })} sx={fieldSx}>
                    <MenuItem value="OPTIONAL">Optional</MenuItem><MenuItem value="REQUIRED">Required</MenuItem>
                  </TextField>
                  <Typography sx={{ fontSize: '11.5px', color: P.muted }}>Derived via cost-center → department</Typography>
                </Box>
              </Stack>
            </SectionCard>

            <SectionCard title="Tax & Compliance">
              <Box sx={grid2}>
                <Toggle label="GST applicable" hint="Account participates in GST computation" checked={f.gstApplicable} onChange={(v) => set({ gstApplicable: v })} />
                <Toggle label="TDS applicable" hint="Account attracts TDS withholding" checked={f.tdsApplicable} onChange={(v) => set({ tdsApplicable: v })} />
              </Box>
            </SectionCard>

            <SectionCard title="Control & Behaviour">
              <Box sx={grid2}>
                <Toggle label="Control account" hint="Sub-ledger control (AR/AP/inventory)" checked={f.isControlAccount} onChange={(v) => set({ isControlAccount: v })} />
                <Toggle label="Require approval" hint="Postings need sign-off" checked={f.requireApproval} onChange={(v) => set({ requireApproval: v })} />
              </Box>
              <Divider sx={{ my: 2, borderColor: P.line }} />
              <FieldLabel text="Notes" />
              <TextField fullWidth size="small" multiline minRows={2} value={f.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Internal notes, assumptions, statutory references…" sx={fieldSx} />
            </SectionCard>
          </Box>

          {/* Preview */}
          <Paper elevation={0} sx={{ ...cardSx, position: { lg: 'sticky' }, top: { lg: 16 } }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${P.line}`, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountTreeOutlined sx={{ fontSize: 18, color: P.primary }} />
              <Typography sx={{ fontSize: '12px', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: P.muted }}>Account Preview</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: 800, color: P.strong }}>{codeUpper || '— — — —'}</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, color: P.strong, mb: 1 }}>{f.accountName || 'Account name'}</Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Chip size="small" label={f.accountType} sx={{ fontWeight: 700, bgcolor: P.primarySoft, color: P.primary }} />
                <Chip size="small" label={f.isGroup ? 'Group' : 'Posting'} sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: P.muted }} />
                <Chip size="small" label={STATEMENT_OF[f.accountType]} variant="outlined" sx={{ borderColor: P.border, color: P.text }} />
              </Stack>
              {[
                ['Normal balance', f.normalBalance === 'DR' ? 'Debit' : 'Credit'],
                ['Parent', parentName || 'Top level'],
                ['Currency', f.currencyPolicy === 'SPECIFIC' ? f.currencyCode : f.currencyPolicy === 'BASE' ? 'Base (INR)' : 'All'],
                ['Reconciliation', f.reconciliationRequired ? `Required · ${f.reconciliationType}` : 'Not required'],
                ['Cost center', f.costCenterRequired ? 'Required' : 'Optional'],
                ['Profit center', f.profitCenterRequired ? 'Required' : 'Optional'],
                ['Project', f.projectRequired ? 'Required' : 'Optional'],
                ['GST / TDS', `${f.gstApplicable ? 'GST' : '—'} / ${f.tdsApplicable ? 'TDS' : '—'}`],
                ['Manual posting', f.allowManualPosting ? 'Allowed' : 'Blocked'],
                ['Approval', f.requireApproval ? 'Required' : 'No'],
                ['Status', f.accountStatus],
              ].map(([k, v]) => (
                <Box key={k as string} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: '5px', borderTop: `1px solid ${P.tline}` }}>
                  <Typography sx={{ fontSize: '12px', color: P.muted }}>{k}</Typography>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: P.strong, textAlign: 'right' }}>{v}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default AccountWorkspacePage;
